import { useMemo, useState } from "react";
import { AddCreditWrapper } from "../styles";
import {
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { User } from "types/user";
import StripeEnvironment from "types/enums/stripeEnvironment";
import { fullname } from "utils";
import { PatchPaymentCustomerInputs } from "hooks/services/subscription/usePatchPaymentCustomer";

interface IProps {
  close: () => void;
  onSubmit: (inputs: Omit<PatchPaymentCustomerInputs, "subscriptionId">) => void;
  users: User[] | null;
  isLoading?: boolean;
}

const EditStripeCustomerModal = ({
  close,
  onSubmit,
  users,
  isLoading,
}: IProps) => {
  const [customerId, setCustomerId] = useState<string>("");
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [stripeEnvironment, setStripeEnvironment] = useState<
    StripeEnvironment | undefined
  >(undefined);

  const activeUsers = useMemo(
    () => users?.filter((user) => user.active) ?? [],
    [users]
  );

  const allGood = useMemo(() => {
    return (
      customerId.trim().length > 0 &&
      userId !== undefined &&
      stripeEnvironment !== undefined
    );
  }, [customerId, userId, stripeEnvironment]);

  const handleSubmit = () => {
    if (!allGood || userId === undefined || stripeEnvironment === undefined)
      return;
    onSubmit({
      stripeCustomerId: customerId.trim(),
      userId,
      stripeEnvironment,
    });
  };

  return (
    <AddCreditWrapper id="add-credit-wrapper" onClick={close}>
      <div
        className="container"
        onClick={(ev) => {
          ev.stopPropagation();
          ev.preventDefault();
        }}
        style={{ gap: 12 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontSize={24} lineHeight={"30px"} fontWeight={700}>
            Update Stripe CustomerId
          </Typography>
          <Close style={{ cursor: "pointer" }} onClick={close} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Typography fontSize={12} fontWeight={600}>
            Customer Id
          </Typography>
          <TextField
            id="stripeCustomerId"
            name="stripeCustomerId"
            value={customerId}
            type="text"
            onChange={(event) => setCustomerId(event.target.value)}
            placeholder="cus_..."
            autoComplete="off"
            variant="standard"
            fullWidth
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Typography fontSize={12} fontWeight={600}>
            User
          </Typography>
          <Select<number | "">
            value={userId ?? ""}
            displayEmpty
            onChange={(ev) => {
              const value = ev.target.value;
              setUserId(value === "" ? undefined : Number(value));
            }}
            MenuProps={{ sx: { zIndex: 119999 } }}
            renderValue={(value) => {
              if (value === "" || value === null || value === undefined) {
                return (
                  <span style={{ opacity: 0.5 }}>Select user</span>
                );
              }
              const user = activeUsers.find((u) => u.id === value);
              return (
                fullname(user?.firstName, user?.lastName) ||
                user?.email ||
                `User #${value}`
              );
            }}
            variant="standard"
          >
            {activeUsers.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {fullname(user.firstName, user.lastName) || user.email}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Typography fontSize={12} fontWeight={600}>
            Stripe Environment
          </Typography>
          <Select<StripeEnvironment | "">
            value={stripeEnvironment ?? ""}
            displayEmpty
            onChange={(ev) => {
              const value = ev.target.value;
              setStripeEnvironment(
                value === "" ? undefined : (value as StripeEnvironment)
              );
            }}
            MenuProps={{ sx: { zIndex: 119999 } }}
            renderValue={(value) => {
              if (!value) {
                return (
                  <span style={{ opacity: 0.5 }}>Select environment</span>
                );
              }
              return value;
            }}
            variant="standard"
          >
            <MenuItem value={StripeEnvironment.Uk}>Uk</MenuItem>
            <MenuItem value={StripeEnvironment.It}>It</MenuItem>
          </Select>
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!allGood}
            >
              Save
            </Button>
          )}
        </div>
      </div>
    </AddCreditWrapper>
  );
};

export default EditStripeCustomerModal;
